import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import type { CampaignSummary, PromptPayQr } from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import { bahtTextToSatang, formatBahtShort, formatSatang } from '../lib/format';
import { useDonor } from '../auth';

/** ยอดที่คนบริจาคบ่อย — ลดการพิมพ์บนมือถือ */
const QUICK_AMOUNTS = [100, 300, 500, 1_000, 2_000, 5_000];

export default function Donate() {
  const donor = useDonor();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState<CampaignSummary[] | null>(null);
  const [campaignId, setCampaignId] = useState<string>('');
  const [amountText, setAmountText] = useState('');
  const [qr, setQr] = useState<PromptPayQr | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const satang = useMemo(() => bahtTextToSatang(amountText), [amountText]);

  useEffect(() => {
    const ac = new AbortController();
    api<{ data: CampaignSummary[] }>('/campaigns/public', { anonymous: true, signal: ac.signal })
      .then((res) => {
        setCampaigns(res.data);
        setCampaignId(res.data.find((c) => c.isDefault)?.id ?? res.data[0]?.id ?? '');
      })
      .catch(() => setCampaigns([]));
    return () => ac.abort();
  }, []);

  // ยอดเปลี่ยน → ขอ payload ใหม่ (QR แบบระบุยอดช่วยลดการโอนผิดจำนวน)
  useEffect(() => {
    const ac = new AbortController();
    const t = setTimeout(() => {
      api<PromptPayQr>(`/campaigns/public/promptpay${buildQuery({ amount: satang ?? undefined })}`, {
        anonymous: true,
        signal: ac.signal,
      })
        .then(setQr)
        .catch(() => {
          if (!ac.signal.aborted) setError('โหลดข้อมูลบัญชีรับบริจาคไม่สำเร็จ');
        });
    }, 400);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [satang]);

  useEffect(() => {
    if (!qr?.payload || !canvasRef.current) return;
    void QRCode.toCanvas(canvasRef.current, qr.payload, {
      width: 240,
      margin: 1,
      errorCorrectionLevel: 'M',
    });
  }, [qr]);

  const selected = campaigns?.find((c) => c.id === campaignId);

  return (
    <div className="screen">
      <div className="topbar">
        <h1>
          ร่วมบริจาค
          <br />
          <span className="muted small">สวัสดีคุณ{donor.displayName}</span>
        </h1>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className="card">
        <h2>เลือกวัตถุประสงค์</h2>
        {campaigns === null ? (
          <p className="muted small" style={{ margin: 0 }}>กำลังโหลด…</p>
        ) : campaigns.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>ยังไม่มีแคมเปญที่เปิดรับบริจาค</p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {campaigns.map((c) => (
              <button
                key={c.id}
                type="button"
                className={campaignId === c.id ? '' : 'secondary'}
                style={{ justifyContent: 'flex-start', textAlign: 'left', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
                onClick={() => setCampaignId(c.id)}
              >
                <span>{c.name}</span>
                {c.goalAmount && (
                  <span className="small" style={{ fontWeight: 400, opacity: 0.85 }}>
                    ระดมได้ {formatBahtShort(c.raisedAmount)} / {formatBahtShort(c.goalAmount)} บาท
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {selected?.description && (
          <p className="muted small" style={{ marginBottom: 0 }}>{selected.description}</p>
        )}
      </div>

      <div className="card">
        <h2>จำนวนเงิน</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
          {QUICK_AMOUNTS.map((baht) => (
            <button
              key={baht}
              type="button"
              className={satang === baht * 100 ? '' : 'secondary'}
              onClick={() => setAmountText(String(baht))}
            >
              {baht.toLocaleString('th-TH')}
            </button>
          ))}
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="amount">หรือระบุเอง (บาท)</label>
          <input
            id="amount"
            type="text"
            inputMode="decimal"
            value={amountText}
            placeholder="ไม่ระบุก็ได้ — กรอกยอดในแอปธนาคารเอง"
            onChange={(e) => setAmountText(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <h2>สแกนจ่ายด้วยพร้อมเพย์</h2>
        {qr?.payload ? (
          <>
            <div style={{ display: 'grid', placeItems: 'center', padding: '8px 0' }}>
              <canvas ref={canvasRef} aria-label="QR พร้อมเพย์" />
            </div>
            {satang ? (
              <p style={{ textAlign: 'center', fontWeight: 700, fontSize: 20, margin: '4px 0' }}>
                {formatSatang(satang)}
              </p>
            ) : (
              <p className="muted small" style={{ textAlign: 'center' }}>
                QR นี้ไม่ระบุยอด — กรอกจำนวนเงินในแอปธนาคาร
              </p>
            )}
          </>
        ) : (
          <p className="muted small">ยังไม่ได้ตั้งค่าพร้อมเพย์ — โอนตามเลขบัญชีด้านล่างได้เลย</p>
        )}

        {qr?.bankAccount && (
          <dl className="detail-list" style={{ marginTop: 10 }}>
            <dt>ธนาคาร</dt>
            <dd>{qr.bankAccount.bankName}</dd>
            <dt>ชื่อบัญชี</dt>
            <dd>{qr.bankAccount.accountName}</dd>
            <dt>เลขบัญชี</dt>
            <dd className="mono">{qr.bankAccount.accountNoMasked}</dd>
          </dl>
        )}
      </div>

      <div className="alert info small">
        เมื่อโอนเรียบร้อยแล้ว กรุณาแจ้งโอนพร้อมแนบสลิป เพื่อให้เจ้าหน้าที่บันทึกยอดและออกใบอนุโมทนาบัตรให้ท่าน
      </div>

      <button
        className="block"
        onClick={() =>
          navigate(
            `/slip${buildQuery({ campaignId, amount: satang ?? undefined, bankAccountId: qr?.bankAccount.id })}`,
          )
        }
      >
        📤 แจ้งโอน + แนบสลิป
      </button>
    </div>
  );
}
